import { useMemo, useState } from 'react'
import type { FallGruppe, FallZeile } from '../../hooks/faelleRegeln'
import { wirkungTon } from '../../hooks/faelleRegeln'
import s from './FaelleListe.module.scss'

/**
 * Die Fälle-Liste — der linke Teil der Master-Detail-Ansicht.
 *
 * Nach Datum ist jede Bewegung eine Zeile (Neuer Fall, Update, geänderte
 * Wahrscheinlichkeit, Beendet) und jeder anstehende Termin eine; nach Titel
 * steht jeder Fall einmal unter seinem Titel. Die Zeile trägt die Kennzahlen
 * des Falls: Wirkung als gefülltes Kästchen, Wahrscheinlichkeit mit
 * Richtung, Last. Ein gefüllter Punkt heißt „neu seit gestern 18:00".
 */
export function FaelleListe({ gruppen, gewaehlt, onWaehle, leer, fuss, fussRechts }: {
  gruppen: FallGruppe[]
  /** Der gewählte Fall — alle seine Zeilen sind markiert. */
  gewaehlt: number | null
  onWaehle: (fallId: number) => void
  leer: string
  /** Links unten, etwa „14 Bewegungen und Termine · 6 Tage". */
  fuss?: string
  /** Rechts unten, etwa die Legende. */
  fussRechts?: string
}) {
  const [zu, setZu] = useState<Set<string>>(new Set())
  const anzahl = useMemo(() => gruppen.reduce((n, g) => n + g.zeilen.length, 0), [gruppen])

  return (
    <div className={s.liste}>
      <div className={s.rollen}>
        {anzahl === 0 && <div className={s.leer}>{leer}</div>}
        {gruppen.map((gruppe) => {
          const offen = !zu.has(gruppe.key)
          return (
            <div key={gruppe.key}>
              <button type="button" className={s.gruppe} aria-expanded={offen}
                      onClick={() => setZu((jetzt) => {
                        const naechste = new Set(jetzt)
                        if (naechste.has(gruppe.key)) naechste.delete(gruppe.key)
                        else naechste.add(gruppe.key)
                        return naechste
                      })}>
                <span className={s.pfeil} aria-hidden="true">{offen ? '▾' : '▸'}</span>
                <span className={s.gruppeLabel}>{gruppe.label}</span>
                <span className={s.gruppeSub}>{gruppe.sub}</span>
                <span className={s.gruppeRechts}>{gruppe.rechts}</span>
              </button>
              {offen && gruppe.zeilen.map((z) => (
                <Zeile key={z.key} zeile={z} aktiv={z.fallId === gewaehlt}
                       onClick={() => onWaehle(z.fallId)} />
              ))}
            </div>
          )
        })}
      </div>
      {(fuss || fussRechts) && (
        <div className={s.fuss}>
          <span>{fuss}</span>
          <span>
            {fussRechts ?? <><span className={s.punktLegende} aria-hidden="true" /> neu seit gestern 18:00 · ↑↓ wechseln</>}
          </span>
        </div>
      )}
    </div>
  )
}

function Zeile({ zeile, aktiv, onClick }: { zeile: FallZeile; aktiv: boolean; onClick: () => void }) {
  return (
    <button type="button" className={s.zeile} data-aktiv={aktiv || undefined}
            onClick={onClick} aria-current={aktiv ? 'true' : undefined}>
      <span className={s.kopf}>
        {zeile.neu && <span className={s.punkt} aria-hidden="true" />}
        <span className={s.ereignis} data-ton={zeile.ereignisTon}>{zeile.ereignis}</span>
        <span className={s.art}>{zeile.art}</span>
        <span className={s.titelKurz}>{zeile.titelKurz}</span>
        <span className={s.rechts} title={zeile.rechtsVoll}>{zeile.rechts}</span>
      </span>
      {zeile.kopf ? (
        <>
          <span className={s.titel}>{zeile.kopf}</span>
          <span className={s.vorgang}>{zeile.vorgang}</span>
        </>
      ) : (
        <span className={s.titel}>{zeile.vorgang}</span>
      )}
      {zeile.termin && (
        <span className={s.terminZeile}>
          <span className={s.terminStatus} data-bestaetigt={zeile.termin.bestaetigt || undefined}>
            {zeile.termin.bestaetigt ? 'bestätigt' : 'unbestätigt'}
          </span>
          <span>erwartete Wesentlichkeit <b>{zeile.termin.wesentlichkeit ?? '–'}</b>/4</span>
          <span className={s.terminIn}>{zeile.termin.inText}</span>
        </span>
      )}
      {zeile.kpi && (
        <span className={s.kpi}>
          <span className={s.wirkung} data-ton={wirkungTon(zeile.kpi.wirkung)}>{zeile.kpi.wirkung}</span>
          <span className={s.trend} data-ton={zeile.kpi.deltaTon}>{zeile.kpi.trend}</span>
          <span className={s.last}>Last {zeile.kpi.last ?? '–'}</span>
          <span className={s.hinweis}>{zeile.kpi.hinweis}</span>
        </span>
      )}
    </button>
  )
}
